import { useCallback, useEffect, useState } from "react";

import { useAppSelectionContext } from "@main/context/App/AppSelectionContext";
import { useSearchParams } from "react-router-dom";

import { decodeAttributesSearchParam, encodeAttributesSearchParam } from "./attributesSearchParam";
import { Attribute } from "./sharedTypes";

type Props = {
  storageKeyPrefix: string;
  defaultAttributes?: Attribute[];
  withoutSearchParams?: boolean;
};

export function useAppAttributes({ storageKeyPrefix, defaultAttributes = [], withoutSearchParams = true }: Props) {
  const { selectedApp } = useAppSelectionContext();
  const storageKey = selectedApp ? `${storageKeyPrefix}_${selectedApp?.id}` : null;
  const [searchParams, setSearchParams] = useSearchParams();

  /// Intentionally may hold `undefined` until `selectedApp` (and therefore `storageKey`)
  /// resolves — the effect below relies on that to avoid encoding an empty filter set into
  /// the URL before the incoming `search` param has been read.
  const [attributes, setAttributes] = useState<Attribute[] | undefined>(() => {
    if (!storageKey) return;
    const urlSearch = searchParams.get("search");
    if (urlSearch && !withoutSearchParams) {
      try {
        const decodedAttributes = decodeAttributesSearchParam(urlSearch);
        localStorage.setItem(storageKey, JSON.stringify({ attributes: decodedAttributes }));
        return decodedAttributes;
      } catch (error) {
        console.error("Error parsing URL search parameter:", error);
        return defaultAttributes;
      }
    }
    return defaultAttributes;
  });

  useEffect(() => {
    if (attributes && !withoutSearchParams) {
      const encodedAttributes = encodeAttributesSearchParam(attributes);
      setSearchParams({ search: encodedAttributes });
    }
  }, [attributes, setSearchParams, withoutSearchParams]);

  useEffect(() => {
    if (!storageKey) return;

    /// `selectedApp` is undefined on the first render, so the `useState` initializer above
    /// can't read the `search` param on a cold load (e.g. a deep link opened in a new tab).
    /// Re-check it here, once the storage key exists, so the URL wins over stored filters.
    const urlSearch = searchParams.get("search");
    if (urlSearch && !withoutSearchParams) {
      try {
        const decodedAttributes = decodeAttributesSearchParam(urlSearch);
        localStorage.setItem(storageKey, JSON.stringify({ attributes: decodedAttributes }));
        setAttributes(decodedAttributes);
        return;
      } catch (error) {
        console.error("Error parsing URL search parameter:", error);
      }
    }

    const storedState = localStorage.getItem(storageKey);
    if (!storedState) {
      localStorage.setItem(storageKey, JSON.stringify({ attributes: defaultAttributes }));
      setAttributes(defaultAttributes);
      return;
    }
    setAttributes(JSON.parse(storedState).attributes);
    // eslint-disable-next-line
  }, [selectedApp]);

  const addAttribute = useCallback(
    (attribute: Attribute) => {
      if (!storageKey) return;
      const storedState = localStorage.getItem(storageKey);
      if (!storedState) {
        const newAttributes = [attribute];
        localStorage.setItem(storageKey, JSON.stringify({ attributes: newAttributes }));
        setAttributes(newAttributes);
        return;
      }
      const newAttributes = [...JSON.parse(storedState).attributes, attribute];
      localStorage.setItem(storageKey, JSON.stringify({ attributes: newAttributes }));
      setAttributes(newAttributes);
    },
    [storageKey],
  );

  const removeAttribute = useCallback(
    (atIndex: number) => {
      if (!storageKey) return;
      const storedState = localStorage.getItem(storageKey);
      if (!storedState) return;

      let newAttributes = [...JSON.parse(storedState).attributes];
      newAttributes.splice(atIndex, 1);
      newAttributes = newAttributes.map((attribute, index) => {
        if (index == 0) return { ...attribute, attributeOperator: undefined };
        return attribute;
      });

      localStorage.setItem(storageKey, JSON.stringify({ attributes: newAttributes }));
      setAttributes(newAttributes);
    },
    [storageKey],
  );

  const updateAttribute = useCallback(
    (atIndex: number, updated: Attribute) => {
      if (!storageKey) return;
      const storedState = localStorage.getItem(storageKey);
      if (!storedState) return;
      const newAttributes = [...JSON.parse(storedState).attributes];
      newAttributes.splice(atIndex, 1, updated);
      localStorage.setItem(storageKey, JSON.stringify({ attributes: newAttributes }));
      setAttributes(newAttributes);
    },
    [storageKey],
  );

  return {
    attributes: attributes || [],
    addAttribute,
    removeAttribute,
    updateAttribute,
  };
}
